import SearchPicker from '../components/SearchPicker';
import { usePickerSearch } from '../hooks/usePickerSearch';
import React, { useState, useCallback, useEffect } from 'react';
import {
  Card,
  Form,
  Button,
  Alert,
  Container,
  Spinner,
  Row,
  Col
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import SettingsPageHeader from '../components/SettingsPageHeader';
import { AssignmentIcon } from '../components/icons/SettingsIcons';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { authAPI, companyAPI } from '../utils/api';
import type {
  CreateUserCompanyAssignmentRequest,
  User,
  Company,
  Role
} from '../types/auth';

const UserCompanyAssignments: React.FC = () => {
  const { currentCompany, userRolePermissionsLoading } = useAuth();
  const {
    isSuperAdmin,
    canViewUsers,
    canViewRoles,
    canAssignUsers,
    canViewCompanies,
    canAccessUserManagement
  } = useRolePermissions();
  const { t } = useTranslation('pages', { keyPrefix: 'userCompanyAssignments' });
  const { t: tc } = useTranslation('common');

  const isCurrentUserSuperAdmin = isSuperAdmin;

  const [assignmentFormData, setAssignmentFormData] = useState<CreateUserCompanyAssignmentRequest>({
    user_id: 0,
    company_id: '',
    role_id: 0
  });

  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentSuccess, setAssignmentSuccess] = useState<string | null>(null);
  const [assignmentError, setAssignmentError] = useState<string | null>(null);

  const [allCompanies, setAllCompanies] = useState<Company[]>([]);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [allCompaniesLoading, setAllCompaniesLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);

  const loadUserOptions = useCallback(async (search: string, signal: AbortSignal): Promise<User[]> => {
    const companyId = assignmentFormData.company_id;
    if (!companyId) return [];
    const response = isCurrentUserSuperAdmin
      ? await authAPI.getUsersGlobalList(companyId, { search, limit: 20 }, signal)
      : await authAPI.getUsers(companyId, { search, limit: 20 }, signal);
    return response.success ? response.data.filter(user => isCurrentUserSuperAdmin || user.superAdminFlag !== 1) : [];
  }, [assignmentFormData.company_id, isCurrentUserSuperAdmin]);
  const userSearch = usePickerSearch(
    `${assignmentFormData.company_id}:${isCurrentUserSuperAdmin}`, loadUserOptions, canViewUsers && !!assignmentFormData.company_id,
  );
  const { setSearch: setUserSearch } = userSearch;
  useEffect(() => {
    if (userSearch.error) setAssignmentError(t('loadUsersFailed'));
  }, [userSearch.error, t]);

  const loadAllCompanies = useCallback(async () => {
    try {
      setAllCompaniesLoading(true);
      const response = await companyAPI.getCompanies();

      if (response.success) {
        setAllCompanies(response.data);
      }
    } catch (error: unknown) {
      console.error('Error loading companies:', error);
      setAssignmentError(t('loadCompaniesFailed'));
    } finally {
      setAllCompaniesLoading(false);
    }
  }, [t]);

  const loadRoles = useCallback(async (companyId: string) => {
    if (!companyId) {
      setAvailableRoles([]);
      return;
    }

    try {
      setRolesLoading(true);
      const response = await authAPI.getRoles(companyId);

      if (response.success) {
        setAvailableRoles(response.data);
      }
    } catch (error: unknown) {
      console.error('Error loading roles:', error);
      setAssignmentError(t('loadRolesFailed'));
    } finally {
      setRolesLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (canViewCompanies) {
      loadAllCompanies();
    }
  }, [loadAllCompanies, canViewCompanies]);

  useEffect(() => {
    if (assignmentFormData.company_id) {
      if (canViewRoles) {
        loadRoles(assignmentFormData.company_id);
      }
    } else {
      setAvailableRoles([]);
    }
  }, [assignmentFormData.company_id, loadRoles, canViewRoles]);

  const handleAssignmentFormChange = useCallback((field: keyof CreateUserCompanyAssignmentRequest, value: string | number) => {
    if (field === 'company_id') setUserSearch('');
    setAssignmentFormData(prev => {
      const newData = { ...prev, [field]: value };

      if (field === 'company_id') {
        newData.user_id = 0;
        newData.role_id = 0;
      }
      else if (field === 'user_id') {
        newData.role_id = 0;
      }

      return newData;
    });

    if (assignmentSuccess) setAssignmentSuccess(null);
    if (assignmentError) setAssignmentError(null);
  }, [assignmentSuccess, assignmentError, setUserSearch]);

  const resetAssignmentForm = useCallback(() => {
    setUserSearch('');
    setAssignmentFormData({
      user_id: 0,
      company_id: '',
      role_id: 0
    });
    setAvailableRoles([]);
  }, [setUserSearch]);

  const handleAssignmentSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!assignmentFormData.company_id) {
      setAssignmentError(t('selectCompanyFirst'));
      return;
    }

    if (!assignmentFormData.user_id) {
      setAssignmentError(t('selectUser'));
      return;
    }

    if (!assignmentFormData.role_id) {
      setAssignmentError(t('selectRole'));
      return;
    }

    try {
      setAssignmentLoading(true);
      setAssignmentError(null);
      setAssignmentSuccess(null);

      const response = await authAPI.createUserCompanyAssignment(assignmentFormData);

      setAssignmentSuccess(response.message || t('createSuccess'));
      resetAssignmentForm();

    } catch (error: unknown) {
      console.error('Error creating assignment:', error);
      const err = error as { response?: { data?: { message?: string }; status?: number }; message?: string };
      setAssignmentError(err.response?.data?.message || err.message || t('saveFailed'));
    } finally {
      setAssignmentLoading(false);
    }
  }, [assignmentFormData, resetAssignmentForm, t]);

  const settingsPath = currentCompany ? `/company/${currentCompany.company_id}/settings` : '/settings';

  if (userRolePermissionsLoading) {
    return (
      <Layout title={t('title')}>
        <Container className="text-center an-enterprise-page">
          <Spinner animation="border" variant="primary" />
        </Container>
      </Layout>
    );
  }

  if (!canAccessUserManagement || !canAssignUsers) {
    return (
      <Layout title={t('title')}>
        <Container className="an-enterprise-page">
          <Alert variant="danger" role="alert">
            {t('accessDenied')}
          </Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      <Container fluid className="an-enterprise-page an-data-page an-settings-page an-page-shell">
        <SettingsPageHeader
          title={t('title')}
          settingsPath={settingsPath}
        />

        <Card className="shadow-sm an-panel an-settings-form-card">
          <Card.Body>
            <div className="d-flex align-items-center mb-4">
              <AssignmentIcon />
              <h5 className="fw-medium text-secondary mb-0">{t('createAssignmentHeading')}</h5>
            </div>

            {assignmentSuccess && (
              <Alert variant="success" role="status" onClose={() => setAssignmentSuccess(null)} dismissible>
                {assignmentSuccess}
              </Alert>
            )}

            {assignmentError && (
              <Alert variant="danger" role="alert" onClose={() => setAssignmentError(null)} dismissible>
                {assignmentError}
              </Alert>
            )}

            <Form onSubmit={handleAssignmentSubmit} autoComplete="off">
              <Row className="g-3">
                <Col xs={12} md={6} lg={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-dark">{t('selectCompanyLabel')}</Form.Label>
                    <Form.Select
                      value={assignmentFormData.company_id}
                      onChange={(e) => handleAssignmentFormChange('company_id', e.target.value)}
                      disabled={assignmentLoading || allCompaniesLoading}
                      aria-label={t('selectCompanyLabel')}
                    >
                      <option value="">{t('chooseCompany')}</option>
                      {allCompanies.map((company) => (
                        <option key={company.company_id} value={company.company_id}>
                          {company.name || company.company_id} (ID: {company.company_id})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col xs={12} md={6} lg={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-dark">{t('selectUserLabel')}</Form.Label>
                    <SearchPicker key={assignmentFormData.company_id} options={userSearch.options} value={assignmentFormData.user_id}
                      getValue={user => user.id}
                      getLabel={user => `${user.name} (${user.email}) ${user.role_name ? `- ${user.role_name}` : ''} ${user.superAdminFlag === 1 ? `(${t('superAdmin')})` : ''}`}
                      onChange={user => handleAssignmentFormChange('user_id', user?.id || 0)}
                      onSearch={setUserSearch} loading={userSearch.loading} error={userSearch.error} onRetry={userSearch.retry}
                      disabled={assignmentLoading || !assignmentFormData.company_id}
                      aria-label={t('selectUserLabel')}
                      placeholder={assignmentFormData.company_id ? t('chooseUser') : t('selectCompanyFirst')} />
                  </Form.Group>
                </Col>

                <Col xs={12} md={6} lg={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-dark">{t('selectRoleLabel')}</Form.Label>
                    <Form.Select
                      value={assignmentFormData.role_id}
                      onChange={(e) => handleAssignmentFormChange('role_id', Number(e.target.value))}
                      disabled={assignmentLoading || rolesLoading || !assignmentFormData.company_id}
                      aria-label={t('selectRoleLabel')}
                    >
                      <option value={0}>
                        {assignmentFormData.company_id ? t('chooseRole') : t('selectCompanyFirst')}
                      </option>
                      {availableRoles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.role_name} ({tc('labels.company')}: {role.company_id})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex flex-column flex-sm-row gap-2 justify-content-end mt-4">
                <Button
                  variant="outline-secondary"
                  onClick={resetAssignmentForm}
                  disabled={assignmentLoading}
                >
                  {t('reset')}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={assignmentLoading}
                >
                  {assignmentLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t('assigning')}
                    </>
                  ) : (
                    <>
                      <AssignmentIcon />
                      {t('createAssignmentBtn')}
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </Layout>
  );
};

export default UserCompanyAssignments;
